import { Component, OnInit, OnDestroy, Renderer2, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs';
import { ScannedActionsSubject, select, Store } from '@ngrx/store';
import { ActionTypes, AutoClientLogin, Login, LoginFailure, LoginWith2FA, LoginWith2FARequired, SupportLogin } from '@app/core/authentication/authentication.actions';
import { AlertService } from '@app/core/services/alert.service';
import { BaseWithBundleComponent } from '@app/shared/components/base/base-with-bundle.component';
import { ComponentServiceBundle } from '@app/core/services/base/component-service-bundle.service';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { PasswordDialogComponent } from '@app/modules/auth/dialogs/password-dialog/password-dialog.component';
import { filter } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { AppModuleRoutes } from '@app/app-routes';

export enum AuthAction {
    RECOVER_PASSWORD = 'recover-password',
    UNLOCK_USER = 'unlock-user'
}

export interface LoginQueryParams {
    action?: string;
    token?: string;
    sessionUser?: string;
    sessionClient?: string;
    sessionToken?: string;
    sessionCountry?: string;
    username?: string;
    clientId?: string;
    autoLogin?: string;
}

type DisplayMode = 'login' | 'login_2fa' | 'forgot_password';

@Component({
    templateUrl: 'login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent extends BaseWithBundleComponent implements OnInit, OnDestroy {
    public loginForm: UntypedFormGroup;
    public loading = false;
    public submitted = false;
    public returnUrl: string;
    public activeUser$: Observable<object>;
    public forgotPasswordMode: boolean;
    public recoveryMailSent: boolean;
    public loginWith2FAMode: boolean;
    public displayMode: DisplayMode;
    public loginWith2FAOptions: any;

    public newCodeTime: number = 16;

    private readonly errorMessages = {
        ACCOUNT_DEACTIVATED: {
            title: marker('login.error.account_deactivated.title'),
            text: marker('login.error.account_deactivated.text')
        },
        ACCOUNT_LOCKED: {
            title: marker('login.error.account_locked.title'),
            text: marker('login.error.account_locked.text')
        },
        BAD_CREDENTIALS: {
            title: marker('login.error.bad_credentials.title'),
            text: marker('login.error.bad_credentials.text')
        },
        WRONG_APP: {
            title: marker('login.error.wrong_application.title'),
            text: marker('login.error.wrong_application.text')
        },
        INVALID_TAN: {
            title: marker('login.error.invalid_tan.title'),
            text: marker('login.error.invalid_tan.text')
        },
        CLIENT_MAINTENANCE: {
            title: marker('login.error.client_maintenance.title'),
            text: marker('login.error.client_maintenance.text')
        }
    };

    private loginErrorResponse: HttpErrorResponse;

    get errorMessage(): { title: string, text: string } | undefined {
        if (this.loginErrorResponse) {
            if (this.loginErrorResponse.error.message === 'wrong-application') {
                return this.errorMessages.WRONG_APP;
            } else if (this.loginErrorResponse.error.resultCode === 'BadCredentials') {
                return this.errorMessages.BAD_CREDENTIALS;
            } else if (this.loginErrorResponse.error.resultCode === 'AccountLocked') {
                if (this.loginErrorResponse.error.message === 'clients.business.login.account-inactive') {
                    return this.errorMessages.ACCOUNT_DEACTIVATED;
                } else {
                    return this.errorMessages.ACCOUNT_LOCKED;
                }
            } else if (this.loginErrorResponse.error.resultCode === 'MultiFactorAuthenticationInvalidTan') {
                return this.errorMessages.INVALID_TAN;
            } else if (this.loginErrorResponse.error.resultCode === 'LoginForbidden') {
                return this.errorMessages.CLIENT_MAINTENANCE;
            }
        }
    }

    get accountLocked(): boolean {
        return this.errorMessage === this.errorMessages.ACCOUNT_LOCKED;
    }

    // convenience getter for easy access to form fields
    get f() { return this.loginForm.controls; }

    constructor(protected componentServiceBundle: ComponentServiceBundle,
        private formBuilder: UntypedFormBuilder,
        private alertService: AlertService,
        private renderer: Renderer2,
        private store: Store<{activeUser: object}>,
        private actions$: ScannedActionsSubject,
        private chRef: ChangeDetectorRef
    ) {
        super(componentServiceBundle);

        // redirect to home if already logged in
        if (this.authenticationService.currentUserValue) {
            this.router.navigateByUrl(AppModuleRoutes.DASHBOARD).then();
        }

        // ngrx:
        this.activeUser$ = store.pipe(select((<any>'user')));

        actions$.pipe(filter(action => action.type === ActionTypes.LoginFailure))
                .subscribe((action: LoginFailure) => {
                    this.loginErrorResponse = action.payload.error;
                });

        actions$.pipe(filter(action => action.type === ActionTypes.LoginWith2FARequired))
                .subscribe((action: LoginWith2FARequired) => {
                    this.loginWith2FAOptions = action.payload;
                    this.showLogin2FAForm();
                });
    }

    public ngOnInit() {
        this.displayMode = 'login';

        this.loginForm = this.formBuilder.group({
            username: ['', Validators.required],
            password: ['', Validators.required],
            code: ['', Validators.required]
        });

        this.updateFormValidators(this.displayMode);

        this.renderer.addClass(document.body, 'iqnity-background-cover-01');
        // get return url from route parameters or default to '/'
        this.returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/dashboard';

        this.handleAuthActions();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        this.renderer.removeClass(document.body, 'iqnity-background-cover-01');
    }

    public onSubmit() {
        this.submitted = true;
        this.clearErrors();

        if (this.loginForm.valid) {
            this.store.dispatch(new Login({username: this.f.username.value, password: this.f.password.value }));
        }
    }

    private handleAuthActions(): void {
        // User authentication actions
        const authAction = (<LoginQueryParams>this.route.snapshot.queryParams).action;
        const processId = (<LoginQueryParams>this.route.snapshot.queryParams).token;

        // Support access params
        const sessionUser = (<LoginQueryParams>this.route.snapshot.queryParams).sessionUser;
        const sessionClient = (<LoginQueryParams>this.route.snapshot.queryParams).sessionClient;
        const sessionToken = (<LoginQueryParams>this.route.snapshot.queryParams).sessionToken;
        const sessionCountry = (<LoginQueryParams>this.route.snapshot.queryParams).sessionCountry;

        // Auto-Client-Login access params
        const username = (<LoginQueryParams>this.route.snapshot.queryParams).username;
        const clientId = (<LoginQueryParams>this.route.snapshot.queryParams).clientId;
        const autoLogin = (<LoginQueryParams>this.route.snapshot.queryParams).autoLogin;

        if (authAction && processId) {

            switch (authAction) {
                case AuthAction.RECOVER_PASSWORD: {
                    setTimeout(() => {
                        const dialogRef = this.dialog.open(PasswordDialogComponent, {
                            width: '800px',
                            disableClose: true,
                            data: {
                                processId: processId
                            }
                        });

                        dialogRef.afterClosed()
                            .subscribe(result => {
                                // No action required
                            });
                    }, 0);

                    break;
                }
                case AuthAction.UNLOCK_USER: {
                    this.authenticationService.unlockUser(processId)
                        .subscribe(unlockResult => {
                            this.showSimpleInfoDialog(marker('login.unlock_password.success.title'), marker('login.unlock_password.success.text'))
                                .subscribe(result => { /*NO ACTION*/ } );
                    }, err => {
                            this.showSimpleInfoDialog(marker('login.unlock_password.error.title'), marker('login.unlock_password.error.text'))
                                .subscribe(result => { /*NO ACTION*/ } );
                    });

                    break;
                }
                default: {
                    break;
                }
            }
        } else if (sessionUser && sessionClient && sessionToken && sessionCountry) {
            this.store.dispatch(new SupportLogin({ supportUser: sessionUser, supportClient: sessionClient, supportToken: sessionToken, supportCountry: sessionCountry}));
        } else if (username && clientId && autoLogin ) {
            this.store.dispatch(new AutoClientLogin({ username: username, clientId: clientId, autoLogin: autoLogin}));
        }
    }

    private clearErrors() {
        this.loginErrorResponse = null;
    }

    public toggleForgotPassword(): void {
        this.submitted = false;
        this.displayMode = this.displayMode == 'login' ? 'forgot_password' : 'login';
        this.clearErrors();

        this.updateFormValidators(this.displayMode);
    }

    public sendRecoveryMail(): void {
        this.submitted = true;

        if (this.loginForm.valid) {
            this.authenticationService.recoverPassword(this.loginForm.get('username').value)
                                      .subscribe(result => {
              this.recoveryMailSent = true;
          }, err => {
              if (err && err.error && err.error.message === 'clients.business.registration.user-not-existing') {
                  this.showSimpleInfoDialog(marker('login.error.recover_password.title'), marker('login.error.recover_password.text'))
                      .subscribe(result => { /*NO ACTION*/ } );
              }
              this.handleError(err);
          });
        }
    }

    public updateFormValidators(mode: DisplayMode): void {

        for (const key in this.f) {
            this.loginForm.get(key).clearValidators();
            this.loginForm.get(key).updateValueAndValidity();
        }

        switch (mode) {
            case 'login_2fa':
                this.loginForm.get('code').setValidators([Validators.required]);
                break;
            case 'forgot_password':
                this.loginForm.get('username').setValidators([Validators.required, Validators.email]);
                break;
            default:
                this.loginForm.get('username').setValidators([Validators.required]);
                this.loginForm.get('password').setValidators(Validators.required);
                break;
        }
    }

    public showLoginForm(): void {
        this.submitted = false;
        this.displayMode = 'login';
        this.clearErrors();

        this.loginForm.setValue({
            'username': this.loginWith2FAOptions.username || '', 
            'password': '',
            'code': ''
        });

        this.updateFormValidators(this.displayMode);
    }

    public showLogin2FAForm(): void {
        this.submitted = false;
        this.displayMode = 'login_2fa';
        this.newCodeTime = 0;
        this.clearErrors();

        this.updateFormValidators(this.displayMode);
    }

    public loginWith2FA(): void {
        this.submitted = true;
        this.clearErrors();

        if (this.loginForm.valid) {
            this.store.dispatch(new LoginWith2FA({
                username: this.loginWith2FAOptions.username,
                token: this.loginWith2FAOptions.accessToken,
                code: this.f.code.value
            }));
        }
    }

    private newCodeTimer: any;
    public sendNewCode(): void {
        
        if (this.newCodeTimer) {
            clearInterval(this.newCodeTimer);
        }
        
        this.store.dispatch(new Login({username: this.loginWith2FAOptions.username, password: this.loginWith2FAOptions.password }));

        this.newCodeTime = 16;
        this.newCodeTimer = setInterval(() => {
            if (--this.newCodeTime < 1) {
                clearInterval(this.newCodeTimer);
            }
            this.chRef.detectChanges();
        }, 1000);
    }
}
